import React, { useEffect, useState } from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from '@amzn/meridian/data-table';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import plusTokens from '@amzn/meridian-tokens/base/icon/plus';
import refreshTokens from '@amzn/meridian-tokens/base/icon/refresh';
import { useTranslation } from 'src/arb-wrapper';
import { PageLoader } from 'src/components/common/loaders/page-loader';
import { ShowingCount } from 'src/components/common/data-table-action-bar/ShowingCount';
import { TablePagination } from 'src/components/common/data-table-action-bar/TablePagination';
import { AddBoxFolderModal } from 'src/components/box-folders/AddBoxFolderModal';
import { BoxFolderTableRow } from 'src/components/box-folders/BoxFolderTableRow';
import { EditBoxFolderExclusionsModal } from 'src/components/box-folders/EditBoxFolderExclusionsModal';
import { useDocumentTitle } from 'src/hooks/useDocumentTitle';
import { useGetSyncTargetsQuery } from 'src/store/bender-api';
import { type SyncTargetDto } from 'src/types/sync-target.types';
import { isBoxFolderSyncMoving } from 'src/utils/boxFolderProgress';

const COLUMN_COUNT = 10;

/** Matches Bender's own default so page 1 is served from its default code path. */
const DEFAULT_PAGE_SIZE = 20;

/**
 * Poll cadence while any visible folder's sync is still moving.
 *
 * Deliberately slower than the 10s used elsewhere in the app: Bender's `GET /sync-targets`
 * runs a full table scan and paginates in memory, so every poll from every open tab scans the
 * whole registry. Qualification takes minutes to tens of minutes, so 30s loses nothing.
 */
const PROGRESS_POLL_INTERVAL_MS = 30_000;

/**
 * Box folders enrolled for asset sync.
 *
 * This is Bender's sync-target registry, presented without that vocabulary: the page
 * never says "sync target" and hides the composite `box:<id>` key. Wire-level naming stays intact in `bender-api.ts` and
 * `sync-target.types.ts`, which mirror Bender's contract.
 *
 * Scope: enrollment is global, not per campaign. Any folder listed here is eligible
 * for every campaign using the "Fetch from Box" template, matched on GTI alone — so
 * removing a folder affects every such campaign. See
 * docs/system-specs/modules/box-folders.md.
 *
 * "Last synced" and "Sync progress" answer different questions and both columns are needed:
 * the timestamp says a folder's discovery finished, the progress cell says how far its files
 * have actually been checked. A folder can show a fresh timestamp with nothing indexed yet.
 */
export const BoxFolders: React.FC = () => {
  const { t } = useTranslation();
  useDocumentTitle(t('box_folders'));

  const [pagination, setPagination] = useState({ page: 1, pageSize: DEFAULT_PAGE_SIZE, pageCount: 1 });
  const [editingTarget, setEditingTarget] = useState<SyncTargetDto | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isSyncMoving, setIsSyncMoving] = useState(false);

  const { data, isLoading, isFetching, isError, refetch } = useGetSyncTargetsQuery(
    {
      page: pagination.page,
      pageSize: pagination.pageSize,
    },
    // Progress only changes while a worker is chewing through a folder, so the page asks for
    // it only then and stops on its own once the last row finishes. Polling unconditionally
    // would scan Bender's whole registry forever behind every open tab.
    { pollingInterval: isSyncMoving ? PROGRESS_POLL_INTERVAL_MS : undefined },
  );

  const paginationResponse = data?.meta?.pagination;
  const syncTargets = data?.data;

  // Fed from the response rather than computed inline, because it is an input to the very
  // query that produces it. Starts false, so the first load never polls, and a folder that
  // finishes syncing turns polling back off without anything having to cancel it.
  useEffect(() => {
    setIsSyncMoving((syncTargets ?? []).some(isBoxFolderSyncMoving));
  }, [syncTargets]);

  // Adopts the server's own view of the page. Guarded rather than falling back to page 1:
  // resetting on an absent response would fight the user's navigation if `data` were ever
  // empty mid-fetch. It is not — `useQuery` keeps the last successful result in `data` when
  // the arg changes (`currentData` is the arg-scoped field) — but the fallback described a
  // reset that never happens, and would turn into a real snap-back the moment anyone read
  // `currentData` here. BoxFolders.pagination.test.tsx pins this against the live hook.
  useEffect(() => {
    if (!paginationResponse) return;
    setPagination((prev) => ({
      ...prev,
      page: paginationResponse.page,
      pageCount: paginationResponse.pageCount,
    }));
  }, [paginationResponse]);

  return (
    <Column>
      {/*
        Controls live in the heading row rather than a DataTableActionBar: that
        component exists to host search, filter tags, and bulk actions, and renders an
        empty 56px band under a divider when given none of them. This page has none —
        GET /sync-targets supports no search or filtering, and there is no bulk mode.
      */}
      <Row alignmentHorizontal="justify" alignmentVertical="center">
        <Heading level={1} type="h500">{t('box_folders')}</Heading>
        <Row alignmentVertical="center" spacing="200">
          <Button
            aria-label={t('box_folder_reload')}
            data-testid="box-folder-reload-button"
            disabled={isFetching}
            type="icon"
            onClick={() => { void refetch(); }}
          >
            <Icon tokens={refreshTokens}>{t('box_folder_reload')}</Icon>
          </Button>
          <Button
            data-testid="box-folder-add-button"
            type="primary"
            onClick={() => { setIsAddOpen(true); }}
          >
            <Icon tokens={plusTokens} />
            {t('box_folder_add')}
          </Button>
        </Row>
      </Row>
      <Text color="secondary" type="b200">{t('box_folders_description')}</Text>

      {isLoading
        ? (
        <Column alignmentHorizontal="center" alignmentVertical="center" height="300px">
          <PageLoader message={t('box_folders_loading')} />
        </Column>
          )
        : isError && !data
          ? (
          // Surfaced as a real error state rather than an empty table: this is the
          // first browser-to-Bender call, so a broken SigV4/CORS path lands here.
          <Box data-testid="box-folders-error" spacingInset="500" type="outline">
            <Column alignmentHorizontal="center" spacing="300">
              <Text type="b300">{t('box_folders_load_error')}</Text>
              <Button data-testid="box-folders-error-reload-button" onClick={refetch}>
                {t('box_folder_reload')}
              </Button>
            </Column>
          </Box>
            )
          : (
          <>
            <Row alignmentVertical="center" spacing="300">
              {/*
                Every field comes from the retained response, never from local state.
                `pagination` advances optimistically on click so the pagination control can
                respond immediately, but `data` still holds the previous page until the next
                one lands — so a count built from local state describes rows that are not on
                screen yet ("Showing 21-40" above page 1's items). Mixing the two is the
                same bug in a subtler form: local `pageSize` with a server `page` misreports
                the range on a page-size change. Read them together or not at all.
              */}
              <ShowingCount
                page={paginationResponse?.page ?? pagination.page}
                pageSize={paginationResponse?.pageSize ?? pagination.pageSize}
                total={paginationResponse?.total ?? 0}
              />
              {isFetching && !isLoading && <Loader size="small" type="linear" />}
            </Row>

            {/*
              No sortColumn/sortDirection/onSort: GET /sync-targets exposes no sort
              params. The server order is fixed at createdAt desc. A sortable-looking
              header that reorders nothing would be a bug.
            */}
            <DataTable
              data-testid="box-folders-table"
              showBorder={true}
              showRowDividers={true}
              size="medium"
              stickyHeaderRows={true}
            >
              <DataTableHead backgroundColor="secondary">
                <DataTableRow>
                  <DataTableHeader width={150}>{t('box_folder_header_folder')}</DataTableHeader>
                  <DataTableHeader width={120}>{t('form_global_status')}</DataTableHeader>
                  <DataTableHeader width={200}>{t('box_folder_header_progress')}</DataTableHeader>
                  <DataTableHeader width={190}>{t('box_folder_header_last_synced')}</DataTableHeader>
                  <DataTableHeader width={130}>{t('box_folder_header_schedule')}</DataTableHeader>
                  <DataTableHeader width={130}>{t('box_folder_header_added_by')}</DataTableHeader>
                  <DataTableHeader width={190}>{t('box_folder_header_added')}</DataTableHeader>
                  <DataTableHeader width={150}>{t('box_folder_header_excluded_folders')}</DataTableHeader>
                  <DataTableHeader width={160}>{t('box_folder_header_excluded_patterns')}</DataTableHeader>
                  <DataTableHeader width={80}>{t('table_header_actions')}</DataTableHeader>
                </DataTableRow>
              </DataTableHead>
              <DataTableBody>
                {syncTargets?.length === 0 && (
                  <DataTableRow>
                    <DataTableCell colSpan={COLUMN_COUNT}>{t('box_folders_empty')}</DataTableCell>
                  </DataTableRow>
                )}
                {syncTargets?.map((syncTarget) => (
                  <BoxFolderTableRow
                    key={syncTarget.syncTargetId}
                    syncTarget={syncTarget}
                    onEditExclusions={setEditingTarget}
                  />
                ))}
              </DataTableBody>
            </DataTable>

            <TablePagination
              currentPage={pagination.page}
              itemsPerPage={pagination.pageSize}
              pageCount={pagination.pageCount}
              onItemsPerPageChange={(pageSize) => {
                setPagination((prev) => ({ ...prev, page: 1, pageSize }));
              }}
              onPageChange={(newPage) => {
                setPagination((prev) => ({ ...prev, page: newPage }));
              }}
            />
          </>
            )}

      {/* Kept mounted-on-demand so the probe query is skipped while closed. */}
      <AddBoxFolderModal open={isAddOpen} onClose={() => { setIsAddOpen(false); }} />
      {editingTarget && (
        <EditBoxFolderExclusionsModal
          open
          syncTarget={editingTarget}
          onClose={() => { setEditingTarget(null); }}
        />
      )}
    </Column>
  );
};
